import { useQuery } from '@tanstack/react-query';

import {
  getCategoryScores,
  getCategoryTrends,
  getDomainRank,
  getChiefComplaint,
  getChiefComplaints,
  getComplaintIndicators,
  getComplaintPlan,
  getComplaintTrends,
  getPlan,
  getDomainSubdomainTrend,
  getIndicatorDescriptions,
  getWhyThisMatters,
  getHealth,
  getMyIdentity,
  getHomeHighlights,
  getIndicatorPerformanceByMonth,
  getOverallStats,
  getRecentActivity,
  getSubcategoryScores,
  type DashboardFilters,
} from '@/api/dashboard';
import { queryKeys } from './queryKeys';

/**
 * The four-domain hero score on the dashboard. Today the API only returns
 * RSQ; the consumer renders FFS/VBC/IP-A as N/A until those datasets exist.
 */
export const useCategoryScores = (filters?: DashboardFilters) =>
  useQuery({
    queryKey: queryKeys.categoryScores(filters),
    queryFn: ({ signal }) => getCategoryScores(filters, signal),
  });

export const useCategoryTrends = (filters?: DashboardFilters) =>
  useQuery({
    queryKey: queryKeys.categoryTrends(filters),
    queryFn: ({ signal }) => getCategoryTrends(filters, signal),
  });

/**
 * Windowed peer rank for one scoring domain (GET /rank/:domain). The API
 * scores every provider over the active filter window with the same formula
 * as the domain score and ranks the signed-in NPI within that list, so the
 * chip follows whatever period is set. Only 'rsq' is scored today.
 */
export const useDomainRank = (domain: string, filters?: DashboardFilters) =>
  useQuery({
    queryKey: queryKeys.domainRank(domain, filters),
    queryFn: ({ signal }) => getDomainRank(domain, filters, signal),
  });

export const useChiefComplaints = (filters?: DashboardFilters) =>
  useQuery({
    queryKey: queryKeys.chiefComplaints(filters),
    queryFn: ({ signal }) => getChiefComplaints(filters, signal),
  });

export const useChiefComplaint = (id: string | undefined, filters?: DashboardFilters) =>
  useQuery({
    queryKey: queryKeys.chiefComplaint(id ?? '', filters),
    queryFn: ({ signal }) => getChiefComplaint(id as string, filters, signal),
    enabled: !!id,
  });

/**
 * Standalone fetch for the Why-This-Matters block. The complaint detail
 * response already inlines this field; this hook exists for surfaces that
 * want the clinical context without paying for the heavy detail query, and
 * as a fallback if the inline block ever goes missing.
 *
 * Cached aggressively (staleTime: Infinity) since the content is static
 * server-side content keyed by slug.
 */
export const useWhyThisMatters = (id: string | undefined) =>
  useQuery({
    queryKey: queryKeys.whyThisMatters(id ?? ''),
    queryFn: ({ signal }) => getWhyThisMatters(id as string, signal),
    enabled: !!id,
    staleTime: Infinity,
  });

export const useComplaintIndicators = (id: string | undefined, filters?: DashboardFilters) =>
  useQuery({
    queryKey: queryKeys.complaintIndicators(id ?? '', filters),
    queryFn: ({ signal }) => getComplaintIndicators(id as string, filters, signal),
    enabled: !!id,
  });

/**
 * Bulk indicator + section descriptions for the Lens info buttons. Fetched
 * once and cached for the session (staleTime: Infinity) since the content is
 * static server-side content, not tenant- or window-scoped.
 */
export const useIndicatorDescriptions = () =>
  useQuery({
    queryKey: queryKeys.indicatorDescriptions(),
    queryFn: ({ signal }) => getIndicatorDescriptions(signal),
    staleTime: Infinity,
  });

export const useSubcategoryScores = (parentId: string | undefined, filters?: DashboardFilters) =>
  useQuery({
    queryKey: queryKeys.subcategoryScores(parentId ?? '', filters),
    queryFn: ({ signal }) => getSubcategoryScores(parentId as string, filters, signal),
    enabled: !!parentId,
  });

/**
 * Tenant-wide chart and documentation counters for the Home hero. Backed by
 * Databricks gold layer via Kai-API /overall-stats.
 */
export const useOverallStats = (filters?: DashboardFilters) =>
  useQuery({
    queryKey: queryKeys.overallStats(filters),
    queryFn: ({ signal }) => getOverallStats(filters, signal),
  });

/**
 * Latest indicator-status events for the Home Recent Activity card.
 * Defaults to the most recent 10 events; pass limit to override.
 */
export const useRecentActivity = (filters?: DashboardFilters & { limit?: number }) =>
  useQuery({
    queryKey: queryKeys.recentActivity(filters),
    queryFn: ({ signal }) => getRecentActivity(filters, signal),
  });

/**
 * Per-indicator score series across N monthly or weekly buckets. Powers the
 * Lens trend tab and the Home domain-trend modal.
 */
export const useIndicatorPerformanceByMonth = (filters?: DashboardFilters) =>
  useQuery({
    queryKey: queryKeys.indicatorPerformanceByMonth(filters),
    queryFn: ({ signal }) => getIndicatorPerformanceByMonth(filters, signal),
  });

/**
 * Trend correlations between the current window and the prior period of equal
 * length (top improving / declining indicators, providers, and chief-complaint
 * volume shifts). Powers the Home "Trends & Highlights" card.
 */
export const useHomeHighlights = (
  filters?: DashboardFilters & { limit?: number; minDenom?: number },
) =>
  useQuery({
    queryKey: queryKeys.homeHighlights(filters),
    queryFn: ({ signal }) => getHomeHighlights(filters, signal),
  });

/**
 * Per-complaint score history for the Trends tab. Returns `available: false`
 * until gold_provider_period_scores is wired into the API.
 */
export const useComplaintTrends = (id: string | undefined, filters?: DashboardFilters) =>
  useQuery({
    queryKey: queryKeys.complaintTrends(id ?? '', filters),
    queryFn: ({ signal }) => getComplaintTrends(id as string, filters, signal),
    enabled: !!id,
  });

/**
 * Documentation-improvement plan for a complaint. Returns `available: false`
 * until plan authoring is wired up; the UI renders an "On Track" empty state
 * in that case.
 */
export const useComplaintPlan = (id: string | undefined, filters?: DashboardFilters) =>
  useQuery({
    queryKey: queryKeys.complaintPlan(id ?? '', filters),
    queryFn: ({ signal }) => getComplaintPlan(id as string, filters, signal),
    enabled: !!id,
  });

/**
 * "View My Plan" (PLA-253). The provider NPI must be threaded in via
 * `filters.provider`; without it the API returns an `available: false`
 * processing state. Pass `enabled: false` (no NPI) to skip the call entirely.
 */
export const usePlan = (filters?: DashboardFilters, enabled = true) =>
  useQuery({
    queryKey: queryKeys.plan(filters),
    queryFn: ({ signal }) => getPlan(filters, signal),
    enabled,
  });

/**
 * Sub-domain breakdown for the Home > RSQ drill-down modal. Returns 5-ish
 * sub-domain rows (History / Physical Exam / MDM / Vital Signs /
 * Labs+Imaging) with current score, 4-month history, and overall rank.
 * Only RSQ is wired today.
 */
export const useDomainSubdomainTrend = (domain: string | undefined, filters?: DashboardFilters) =>
  useQuery({
    queryKey: ['kai', 'domain-subdomain-trend', domain, filters ?? {}] as const,
    queryFn: ({ signal }) => getDomainSubdomainTrend(domain as string, filters, signal),
    enabled: !!domain,
  });

/**
 * Backend health check. Used by the Dev banner; not consumed elsewhere.
 */
export const useHealth = () =>
  useQuery({
    queryKey: queryKeys.health(),
    queryFn: ({ signal }) => getHealth(signal),
  });

/**
 * Who the API thinks the signed-in user is, including provider scope.
 * `providerScoped: false` (or `npi: null`) drives the missing-NPI notice.
 * Identity only changes on re-login, so cache aggressively and don't retry
 * noisy failures — the notice must never block or spam the dashboard.
 */
export const useMyIdentity = () =>
  useQuery({
    queryKey: queryKeys.myIdentity(),
    queryFn: ({ signal }) => getMyIdentity(signal),
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });
